import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MapPin, Rocket, Pause, Copy, Ticket, PlusCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminVendorPilots() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [invites, setInvites] = useState(null);
  const [count, setCount] = useState(1);

  const load = () => api.get("/admin/pilots").then((r) => setRows(r.data.pilots || []));
  useEffect(() => { load(); }, []);

  const openPilot = async (pilot) => {
    setOpen(pilot);
    const response = await api.get(`/admin/pilots/${pilot.pilot_id}/invites`);
    setInvites(response.data);
  };

  const issue = async () => {
    try {
      await api.post(`/admin/pilots/${open.pilot_id}/invites/issue`, { count });
      toast.success(`${count} invite(s) issued`);
      const response = await api.get(`/admin/pilots/${open.pilot_id}/invites`);
      setInvites(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not issue invites");
    }
  };

  const activate = async (pilot_id) => {
    await api.post(`/admin/pilots/${pilot_id}/activate`, {});
    toast.success("Pilot activated");
    load();
    if (open?.pilot_id === pilot_id) {
      const response = await api.get(`/admin/pilots/${pilot_id}/invites`);
      setInvites(response.data);
    }
  };

  const pause = async (pilot_id) => {
    await api.post(`/admin/pilots/${pilot_id}/pause`, {});
    toast.success("Pilot paused");
    load();
  };

  const revoke = async (code) => {
    try {
      await api.post(`/admin/pilots/invites/${code}/revoke`, {});
      toast.success("Invite revoked");
      const response = await api.get(`/admin/pilots/${open.pilot_id}/invites`);
      setInvites(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not revoke");
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success("Code copied");
  };

  if (rows === null) return <Loading />;
  const legalInactive = rows[0] && rows[0].redemption_blocked_reason === "legal_terms_not_active";

  return (
    <div className="p-6 space-y-6" data-testid="admin-vendor-pilots">
      <PageHeader title="Resident Technician Pilots" description="Invite-only pilots. Bookings remain disabled until the agreement and schedule are promoted to ACTIVE." />
      {legalInactive && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="legal-inactive-banner">
          <b>Invites are inactive.</b> The vendor agreement and commercial schedule are still DRAFT. Codes can be issued and tracked here, but they cannot be redeemed by applicants until both are published. Promote both in <code>/admin/vendor-legal</code> after your business approval.
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title="No pilots" />
      ) : (
        <table className="w-full text-sm border">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-2 text-left">Pilot</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Region</th>
              <th className="p-2 text-left">Categories</th>
              <th className="p-2 text-left">Cap</th>
              <th className="p-2 text-left">Invites</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.pilot_id} className="border-t" data-testid={`pilot-row-${row.slug}`}>
                <td className="p-2 font-medium">{row.name}</td>
                <td className="p-2"><Badge variant={row.status === "ACTIVE" ? "default" : "secondary"}>{row.status}</Badge></td>
                <td className="p-2 flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" /> {row.region?.city}, {row.region?.district}, {row.region?.state}</td>
                <td className="p-2 text-xs">{(row.categories || []).join(", ")}</td>
                <td className="p-2 text-xs">{row.cap?.approved}/{row.cap?.max}</td>
                <td className="p-2 text-xs">
                  <Badge variant="outline">Issued: {row.invite_counts?.issued || 0}</Badge>{" "}
                  <Badge variant="outline">Redeemed: {row.invite_counts?.redeemed || 0}</Badge>
                </td>
                <td className="p-2 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openPilot(row)} data-testid={`pilot-open-${row.slug}`}>
                    <Ticket className="h-4 w-4" /> Invites
                  </Button>
                  {row.status === "DRAFT" && (
                    <Button size="sm" onClick={() => activate(row.pilot_id)} data-testid={`pilot-activate-${row.slug}`}>
                      <Rocket className="h-4 w-4" /> Activate
                    </Button>
                  )}
                  {row.status === "ACTIVE" && (
                    <Button variant="outline" size="sm" onClick={() => pause(row.pilot_id)} data-testid={`pilot-pause-${row.slug}`}>
                      <Pause className="h-4 w-4" /> Pause
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Invite codes — {open?.name}</DialogTitle>
            <DialogDescription>Super Admin only. Codes are inactive until the agreement and schedule are promoted to ACTIVE.</DialogDescription>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={50} value={count} onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)} className="w-24" data-testid="pilot-invite-count" />
                <Button onClick={issue} data-testid="pilot-invite-issue">
                  <PlusCircle className="h-4 w-4" /> Issue
                </Button>
                <p className="text-xs text-slate-500">Cap: {invites?.cap?.approved}/{invites?.cap?.max} approved, {invites?.cap?.remaining} slot(s) left.</p>
              </div>
              {!invites ? <Loading /> : (
                <table className="w-full text-sm border">
                  <thead className="bg-slate-50">
                    <tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Expires</th><th className="p-2 text-left">Note</th><th className="p-2 text-left">Actions</th></tr>
                  </thead>
                  <tbody>
                    {invites.invites.map((inv) => {
                      const effStatus = inv.effective_status || inv.status;
                      return (
                        <tr key={inv.invite_id} className="border-t">
                          <td className="p-2 font-mono text-xs">{inv.code}</td>
                          <td className="p-2">
                            <Badge variant={effStatus === "ISSUED" ? "default" : effStatus === "REDEEMED" ? "secondary" : "destructive"}>{effStatus}</Badge>
                            {invites.legal_terms_active === false && effStatus === "ISSUED" && (
                              <span className="ml-2 text-[11px] text-amber-700">inactive — terms DRAFT</span>
                            )}
                          </td>
                          <td className="p-2 text-xs">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}</td>
                          <td className="p-2 text-xs">{inv.note || "—"}</td>
                          <td className="p-2 flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => copy(inv.code)} data-testid={`pilot-invite-copy-${inv.code}`}>
                              <Copy className="h-4 w-4" /> Copy
                            </Button>
                            {inv.status === "ISSUED" && (
                              <Button variant="destructive" size="sm" onClick={() => revoke(inv.code)} data-testid={`pilot-invite-revoke-${inv.code}`}>
                                Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
